'use client';

import type { PaylinkStatus } from '@packages/order/src/utils/paylinkStatus';
import { getStatus, Mode } from '@packages/order/src/utils/paylinkStatus';
import { PaylinkInit } from '@packages/order/src/components/Paylink/PaylinkInit.client';
import { FormattedMessage } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useSearchParams } from 'next/navigation';
import { PaylinkSkeleton } from '@packages/order/src/components/Paylink/PaylinkSkeleton';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';

/**
 * This page reads the paylink status from the session storage and if the status is present, it will initialize the paylink process and show the amount, available payment methods and the pay button
 * example: https://baurde.empirie.dev/paylink/data/;Whp8bGaAsvssLtNCkwgQQXWkyrQYq9yDqeRxIscDJbPe38Tc2tJuvx1bsSwsST16vmgTuSr32cUvhvLT1aJZsUFZ4iur;kwlJ0NB9D8NbOFL:9gKv81y5KRQSSIKXivpOfMT0p1dLfgSP5d1iTvSPG7DEWRlbfY6oJ0zFAYlyGqGM7QQabkDgExKC2lZhEruFgha6PDo:aWQfrcNN8gva14wy81ScFVqGEKD:KOjlbrFMXga6:VHHESUTbJxwfK4H86w6C8C12Q8:bNgA:KTGiCb7hTk78NjLNZPFjGni7wgIx2ykHQYWkv.5Op;qoUqxD6ZjNpiWNpBJQ==
 */
const PaylinkPage = () => {
  const [status, setStatus] = useState<PaylinkStatus>();
  const dispatchGTMEvent = useTracking();
  const { addNotification } = useNotification();
  const searchParams = useSearchParams();

  const message = searchParams?.get('message');

  useEffect(() => {
    const newStatus = getStatus();
    setStatus(newStatus);
    dispatchGTMEvent({
      event: 'pageMetaData',
      Seitentyp: 'myaccount',
      page: {
        type: 'myaccount',
        template: 'paylink',
      },
    });
  }, [dispatchGTMEvent]);

  useEffect(() => {
    if (message) {
      // needs to be fixed in snackbar context... on every route change the toasts are reset, so the timeout is a ugly but necessary workaround
      setTimeout(() => addNotification({ message, severity: 'error', duration: 5000 }), 1000);
    }
    // needs to be fixed in snackbar context... addToast function can not be in the dependency array, because the whole context payload is reinitialized on every toasts manipulation, so the addToast function gets a new reference and the hook triggers in an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  if (status && status.url === '') {
    return (
      <div data-testid="paylink-page">
        <Notification severity="error">
          <FormattedMessage
            key="Paylink.error.invalidLink"
            id="Paylink.error.invalidLink"
            defaultMessage="Dieser Link ist leider nicht mehr gültig."
          />
        </Notification>
      </div>
    );
  }

  if (status?.mode === Mode.INITIALIZE) {
    return <PaylinkInit status={status} />;
  }

  return <PaylinkSkeleton />;
};

// eslint-disable-next-line import-x/no-default-export
export default PaylinkPage;
